import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Buscador de pacientes para diálogos.
 * Filtra por nombre, apellido, DNI o teléfono según escribe.
 *
 * Props:
 * - patients: array de pacientes ({id, patient_number, name, surname, dni, phone})
 * - value: id seleccionado (o "" para ninguno)
 * - onChange: (id|null) => void  (devuelve null si se limpia)
 * - placeholder
 * - testid: prefijo para data-testid
 */
export default function PatientCombobox({ patients, value, onChange, placeholder = "Buscar paciente…", testid = "patient-combobox" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => patients.find((p) => p.id === value) || null,
    [patients, value],
  );

  // Filtrado eficiente en cliente
  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return patients.slice(0, 50);
    return patients
      .filter((p) => {
        const hay = `${p.name || ""} ${p.surname || ""} ${p.dni || ""} ${p.phone || ""} ${p.phone_alt || ""} #${p.patient_number || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 100);
  }, [patients, query]);

  const label = selected
    ? `#${selected.patient_number} · ${selected.name} ${selected.surname || ""}`.trim()
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={`${testid}-trigger`}
          className={cn(
            "w-full justify-between bg-white font-normal",
            !selected && "text-[#9CA3AF]",
          )}
        >
          <span className="truncate flex items-center gap-2">
            <Search className="h-4 w-4 shrink-0 text-[#6E7772]" />
            {label}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Nombre, DNI, teléfono…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testid}-input`}
          />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                  setQuery("");
                }}
                data-testid={`${testid}-clear`}
              >
                <span className="text-[#6E7772]">— Sin paciente —</span>
              </CommandItem>
              {filtered.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.id}
                  onSelect={() => {
                    onChange(p.id);
                    setOpen(false);
                    setQuery("");
                  }}
                  data-testid={`${testid}-item-${p.id}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === p.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      #{p.patient_number} · {p.name} {p.surname || ""}
                    </span>
                    <span className="text-xs text-[#6E7772]">
                      {p.dni || "—"} {p.phone ? `· ${p.phone}` : ""}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
